import React, { useState } from 'react';
import Modal from 'react-modal';
import Select from "react-select";
import InfoIconButton from '../../UsefulComponent/InfoIconButton';
import './conditionModal.scss';

Modal.setAppElement('#root');

const ConditionComponent = ({
  formData,
  setFormData,
  disableSelect,
  forCreateCardForm,
  t,
  tabindex,
  formSelectStyles,
  // NEW: choose between "sports" (default) and "tcg"
  conditionProfile = "sports",
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSelectChange = (event) => {
    setFormData({ ...formData, ConditionCard: event.target.value });
  };

  const openModal = (event) => {
    event.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const isTCG = conditionProfile === "tcg";

  // ---------- OPTIONS ----------
  const sportsConditionOptions = [
    { value: "NM", label: t("Near Mint or Better") },
    { value: "EX", label: t("Excellent") },
    { value: "VG", label: t("Very Good") },
    { value: "FP", label: t("Poor") }, // FP in value but “Poor” in label
  ];

  const tcgConditionOptions = [
    { value: "NM",  label: t("Near Mint (NM)") },
    { value: "LP",  label: t("Lightly Played (LP)") },
    { value: "MP",  label: t("Moderately Played (MP)") },
    { value: "HP",  label: t("Heavily Played (HP)") },
    { value: "DMG", label: t("Damaged (DMG)") },
  ];

  const conditionOptions = isTCG ? tcgConditionOptions : sportsConditionOptions;

  // Value object for react-select
  const currentValue =
    disableSelect || !formData?.ConditionCard
      ? null
      : {
          value: formData.ConditionCard,
          label:
            conditionOptions.find(
              (opt) => opt.value === formData.ConditionCard
            )?.label || "",
        };

  return (
    <>
      <label>
        {t("Condition")}{" "}
        <InfoIconButton onClick={openModal} size={36} />
      </label>

      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Select
          className="form-control"
          styles={formSelectStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          menuPlacement="auto"
          isSearchable={false}
          isDisabled={disableSelect}
          onChange={(selected) =>
            handleSelectChange({ target: { value: selected?.value } })
          }
          value={currentValue}
          options={[
            ...(forCreateCardForm ? [] : [{ value: "", label: "" }]),
            ...conditionOptions,
          ]}
          tabIndex={tabindex}
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={{
          overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)' },
          content: {
            width: '80%',
            maxWidth: 'fit-content',
            maxHeight: 'fit-content',
            margin: 'auto',
            marginTop: '50px',
            padding: '20px',
            color: 'white',
            background: 'black',
            borderRadius: '10px',
            zIndex: '1000',
            textAlign: 'left',
          },
        }}
      >
        <div className="cond-modal">
          <h2 className="cond-modal__title">{t("Card Condition Details")}</h2>

          <div className="cond-modal__body">
            {isTCG ? (
              <>
                {/* TCG SCALE: NM / LP / MP / HP / DMG */}
                <div className="cond-modal__section">
                  <h3><span className="chip chip--nm">{t("Near Mint (NM)")}</span></h3>
                  <ul className="cond-modal__list">
                    <li>{t("Looks almost pack fresh")}</li>
                    <li>{t("Very minor corner or edge wear only")}</li>
                  </ul>
                </div>

                <div className="cond-modal__section">
                  <h3><span className="chip chip--lp">{t("Lightly Played (LP)")}</span></h3>
                  <ul className="cond-modal__list">
                    <li>{t("Small signs of shuffle wear")}</li>
                    <li>{t("Minor whitening on edges or corners")}</li>
                  </ul>
                </div>

                <div className="cond-modal__section">
                  <h3><span className="chip chip--mp">{t("Moderately Played (MP)")}</span></h3>
                  <ul className="cond-modal__list">
                    <li>{t("Noticeable wear across the card")}</li>
                    <li>{t("Small creases or heavier edge wear")}</li>
                  </ul>
                </div>

                <div className="cond-modal__section">
                  <h3><span className="chip chip--hp">{t("Heavily Played (HP)")}</span></h3>
                  <ul className="cond-modal__list">
                    <li>{t("Major wear, whitening and/or creases")}</li>
                    <li>{t("Likely heavy play or storage damage")}</li>
                  </ul>
                </div>

                <div className="cond-modal__section">
                  <h3><span className="chip chip--dmg">{t("Damaged (DMG)")}</span></h3>
                  <ul className="cond-modal__list">
                    <li>{t("Severe damage such as tears, water, ink, or bends")}</li>
                    <li>{t("Best suited as a filler or binder copy")}</li>
                  </ul>
                </div>
              </>
            ) : (
              <>
                {/* ORIGINAL SPORTS SCALE: NM / EX / VG / FP */}
                <div className="cond-modal__section">
                  <h3><span className="chip chip--nm">{t("Near Mint or Better")}</span></h3>
                  <ul className="cond-modal__list">
                    <li>{t("Comparable to a fresh pack")}</li>
                    <li>{t("Minor corner and edge wear")}</li>
                  </ul>
                </div>

                <div className="cond-modal__section">
                  <h3><span className="chip chip--ex">{t("Excellent")}</span></h3>
                  <ul className="cond-modal__list">
                    <li>{t("Has clearly visible signs of wear")}</li>
                    <li>{t("Moderate surface scuffing")}</li>
                    <li>{t("Fuzzy corners")}</li>
                    <li>{t("Moderate edge chipping")}</li>
                  </ul>
                </div>

                <div className="cond-modal__section">
                  <h3><span className="chip chip--vg">{t("Very Good")}</span></h3>
                  <ul className="cond-modal__list">
                    <li>{t("Has moderate-to-heavy damage all over")}</li>
                    <li>{t("Moderate creasing")}</li>
                    <li>{t("Moderate to heavy edge chipping")}</li>
                    <li>{t("Rounding and soft corners")}</li>
                    <li>{t("Moderate discoloration")}</li>
                  </ul>
                </div>

                <div className="cond-modal__section">
                  <h3><span className="chip chip--fp">{t("Poor")}</span></h3>
                  <ul className="cond-modal__list">
                    <li>{t("Is extremely worn and displays flaws all over")}</li>
                    <li>{t("Major creasing")}</li>
                    <li>{t("Moderate discoloration")}</li>
                    <li>{t("Major border wear")}</li>
                    <li>{t("Heavily worn and rounded corners")}</li>
                  </ul>
                </div>
              </>
            )}
          </div>

          <footer className="cond-modal__footer">
            <button type="button" className="btn-primary" onClick={closeModal}>
              {t("Close")}
            </button>
          </footer>
        </div>
      </Modal>
    </>
  );
};

export default ConditionComponent;
